import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Heart, ShieldCheck, Sun, Users, MapPin, Sparkles, ArrowRight, Award } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
};

const fmtRange = (start, end) => {
  if (!start || !end) return "";
  const s = new Date(start);
  const e = new Date(new Date(end).getTime() - 1);
  return `${s.toLocaleDateString("en-AU", { day: "numeric", month: "short" })} – ${e.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}`;
};

export default function WeeklyWholesome() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get("/weekly-wholesome")
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatApiError(e)));
  }, []);

  if (err)
    return (
      <div className="text-ochre-300 min-h-[40vh] flex items-center justify-center" data-testid="weekly-error">
        {err}
      </div>
    );
  if (!data)
    return (
      <div className="text-ink/50 min-h-[40vh] flex items-center justify-center" data-testid="weekly-loading">
        Gathering this week's kindness…
      </div>
    );

  const stats = data.stats || {};
  const featured = data.featured_member;
  const acts = data.top_acts || [];

  return (
    <div className="max-w-4xl mx-auto space-y-10" data-testid="weekly-page">
      {/* Hero */}
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-ochre-300 font-mono">Weekly Wholesome</div>
        <h1
          className="font-display text-5xl text-ink leading-tight mt-2"
          style={{ fontFamily: "Fraunces", fontWeight: 400 }}
        >
          Kindness of the <em className="italic text-ivy">week.</em>
        </h1>
        <p className="text-ink/70 mt-3 max-w-2xl">
          A gentle weekly digest of what our members quietly do for each other. Nothing viral. Nothing loud. Just proof
          that kindness is still ordinary here.
        </p>
        <div className="mt-3 text-xs font-mono text-ink/50 tracking-widest">{fmtRange(data.week_starts, data.week_ends)}</div>
      </div>

      {/* Community stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { key: "kind_acts", label: "Kind acts", icon: Heart },
          { key: "gratitude", label: "Gratitude", icon: Sun },
          { key: "vouches", label: "Vouches", icon: ShieldCheck },
          { key: "place_moments", label: "Places", icon: MapPin },
          { key: "active_members", label: "Members", icon: Users },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.key} className="card-paper p-4 text-center" data-testid={`weekly-stat-${s.key}`}>
              <Icon size={16} className="mx-auto text-ivy" />
              <div className="text-3xl font-display text-ink mt-1" style={{ fontFamily: "Fraunces" }}>
                {stats[s.key] || 0}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-ink/50 mt-1">{s.label}</div>
            </div>
          );
        })}
      </div>

      {/* Featured member */}
      {featured && (
        <div
          className="card-paper p-8 relative overflow-hidden"
          style={{ border: "2px double #D4A574" }}
          data-testid="weekly-featured"
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-ochre-300 font-mono">
            <Award size={14} className="text-ochre-300" /> This week's most-kind member
          </div>
          <h2
            className="mt-3 font-display italic text-ink"
            style={{ fontFamily: "Fraunces", fontSize: 48, fontWeight: 400, lineHeight: 1 }}
          >
            {featured.first_name}.
          </h2>
          <div className="mt-2 text-xs text-ochre-300 font-mono tracking-widest">
            LICENSE {featured.license_no}
            {featured.license_status === "licensed" && <span className="ml-2 text-ivy">· CHARACTER VERIFIED</span>}
          </div>
          <blockquote
            className="mt-4 italic text-ink/80 text-lg leading-relaxed"
            style={{ fontFamily: "Fraunces" }}
          >
            {featured.why_featured}.
          </blockquote>
          <div className="mt-6 flex flex-wrap gap-4">
            <div className="text-sm text-ink/60">
              <span className="font-display text-ink text-2xl mr-1" style={{ fontFamily: "Fraunces" }}>
                {featured.stats?.total || 0}
              </span>
              lifetime chapters in their Ledger
            </div>
            <Link
              to={`/ledger/${featured.license_no}`}
              className="btn-pill bg-ink text-parchment hover:bg-ivy inline-flex items-center gap-2 ml-auto"
              data-testid="weekly-featured-cta"
            >
              Read their Kind Ledger <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      )}

      {/* Top kind acts */}
      <div>
        <div className="eyebrow">Most-hearted this week</div>
        <div className="mt-4 space-y-4" data-testid="weekly-top-acts">
          {acts.length === 0 && (
            <div className="text-ink/50 italic" data-testid="weekly-empty">
              A quiet week. Be the first to add an act of kindness.
            </div>
          )}
          {acts.map((a, i) => (
            <div key={a.id} className="card-paper p-6 flex items-start gap-5" data-testid={`weekly-act-${a.id}`}>
              <div className="text-5xl font-display italic text-ochre-200 shrink-0 leading-none" style={{ fontFamily: "Fraunces" }}>
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <blockquote className="font-display italic text-ink text-lg leading-relaxed" style={{ fontFamily: "Fraunces" }}>
                  &ldquo;{a.text}&rdquo;
                </blockquote>
                <div className="mt-3 flex items-center justify-between text-xs">
                  <div className="text-ink/60">
                    — {a.first_name || "A Wholesome member"}
                    {a.license_no && <span className="font-mono ml-1 text-ink/40">· {a.license_no}</span>}
                    <span className="text-ink/40 ml-2 font-mono">· {fmtDate(a.at)}</span>
                  </div>
                  <div className="flex items-center gap-1 text-rose-500">
                    <Heart size={14} fill="currentColor" />
                    <span className="text-ink font-medium">{a.hearts}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="text-center pt-6 border-t border-ochre-100">
        <Link
          to="/app/ledger"
          className="btn-pill bg-ivy text-parchment hover:bg-ivy-600 inline-flex items-center gap-2"
          data-testid="weekly-add-cta"
        >
          <Sparkles size={14} /> Add to your own Ledger
        </Link>
      </div>
    </div>
  );
}
